import Link from 'next/link'
import clsx from 'clsx'
import { Pillow } from '@/components/Pillow/Pillow'
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Text } from 'ui/src/components/Text/Text'
import {
  navigationLink,
  productNavigationLink,
  productNavigationLinkCard,
  secondaryNavigationLink,
} from './NavigationLink.css'

export type NavigationLinkProps = Pick<HTMLAnchorElement, 'href'> &
  Omit<NavigationMenuPrimitive.NavigationMenuLinkProps, 'href'> & {
    pillowImageSrc?: string
    label?: string
    absoluteUrl?: boolean
  }

export const NavigationLink = ({ href, className, children, ...rest }: NavigationLinkProps) => {
  // Render a regular <a> tag for manual URLs containing https://. Thats because when linking between locales (/no -> /se),
  // you end up on /no/se with using Next internal routing. This will also work for external links.
  const isExternalLink = /^(https?:)?\/\//.exec(href)
  if (isExternalLink) {
    return (
      <NavigationMenuPrimitive.Link href={href} className={clsx(navigationLink, className)} {...rest}>
        {children}
      </NavigationMenuPrimitive.Link>
    )
  }

  return (
    <NavigationMenuPrimitive.Link className={clsx(navigationLink, className)} asChild {...rest}>
      <Link href={href}>{children}</Link>
    </NavigationMenuPrimitive.Link>
  )
}

export const ProductNavigationLink = ({ href, children, pillowImageSrc, label }: NavigationLinkProps) => {
  return (
    <div className={productNavigationLinkCard}>
      <Pillow size="xsmall" src={pillowImageSrc} />
      <NavigationMenuPrimitive.Link asChild>
        <Link href={href} className={productNavigationLink}>
          <Text as="span" size={{ _: 'xl', lg: 'md' }}>
            {children}
          </Text>
        </Link>
      </NavigationMenuPrimitive.Link>
      {label && (
        <Badge size="responsive" as="span" variant="green1">
          {label}
        </Badge>
      )}
    </div>
  )
}

export const SecondaryNavigationLink = ({ href, className, children, ...rest }: NavigationLinkProps) => {
  return (
    <NavigationMenuPrimitive.Link className={clsx(secondaryNavigationLink, className)} asChild {...rest}>
      <Link href={href}>{children}</Link>
    </NavigationMenuPrimitive.Link>
  )
}
